import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Bars3Icon, 
  XMarkIcon,
  HomeIcon,
  InformationCircleIcon,
  WrenchScrewdriverIcon,
  BriefcaseIcon,
  PhotoIcon,
  UsersIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  PhoneIcon
} from '@heroicons/react/24/outline';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/', icon: HomeIcon },
    { name: 'About', href: '/about', icon: InformationCircleIcon },
    { name: 'Services', href: '/services', icon: WrenchScrewdriverIcon },
    { name: 'Projects', href: '/projects', icon: BriefcaseIcon },
    { name: 'Gallery', href: '/gallery', icon: PhotoIcon },
    { name: 'Management', href: '/management', icon: UsersIcon },
    { name: 'Blog', href: '/blog', icon: DocumentTextIcon },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <>
      {/* Contact Info Bar - desktop only */}
      <div className="hidden sm:block bg-primary text-white text-xs sm:text-sm py-1.5 sm:py-2 px-0 fixed top-0 left-0 right-0 z-50 w-full">
        <div className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-row justify-between items-center">
            {/* Mobile: phone only left-aligned | Desktop: email left */}
            <div className="flex items-center space-x-2">
              <EnvelopeIcon className="hidden sm:block h-4 w-4 flex-shrink-0" />
              <a href="mailto:info@plencoplumbingltd.com" className="hidden sm:block hover:underline truncate">
                info@plencoplumbingltd.com
              </a>
              <PhoneIcon className="block sm:hidden h-3.5 w-3.5 flex-shrink-0" />
              <a href="tel:+256200906397" className="block sm:hidden hover:underline whitespace-nowrap">
                +256 200 906397
              </a>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="hidden sm:flex items-center space-x-2">
                <PhoneIcon className="h-4 w-4 flex-shrink-0" />
                <div className="flex items-center gap-2">
                  <a href="tel:+256200906397" className="hover:underline whitespace-nowrap">+256 200 906397</a>
                  <span className="text-primary-200 font-light">|</span>
                  <a href="tel:+256704485986" className="hover:underline whitespace-nowrap">+256 704 485986</a>
                  <span className="text-primary-200 font-light">|</span>
                  <a href="tel:+256786272003" className="hover:underline whitespace-nowrap">+256 786 272003</a>
                </div>
              </div>
              <div className="hidden sm:flex items-center space-x-2">
                <a href="https://www.facebook.com/people/Plenco-Plumbing-Construction-Company-Ltd/100088949889828/" target="_blank" rel="noopener noreferrer" className="hover:text-primary-200 transition-colors" aria-label="Facebook">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
                <a href="https://x.com/PlencoPlumbingC" target="_blank" rel="noopener noreferrer" className="hover:text-primary-200 transition-colors" aria-label="Twitter/X">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/96765788" target="_blank" rel="noopener noreferrer" className="hover:text-primary-200 transition-colors" aria-label="LinkedIn">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <nav className="bg-white shadow-md fixed top-0 sm:top-[34px] left-0 right-0 z-40 w-full">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-0">
          <div className="flex justify-between items-center h-16 sm:h-14">
          <div className="flex-shrink-0 flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <img 
                src="/images/gallery/cropped-Logo-1.png" 
                alt="Plenco Plumbing & Construction Company Logo" 
                className="h-10 sm:h-12 w-auto"
              />
            </Link>
          </div>

          <div className="hidden lg:block">
            <div className="ml-10 flex items-baseline space-x-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 flex items-center space-x-1 ${
                    isActive(item.href)
                      ? 'bg-primary text-white'
                      : 'text-gray-700 hover:bg-primary-50 hover:text-primary'
                  }`}
                >
                  <item.icon className="h-4 w-4" />
                  <span>{item.name}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="hidden lg:block">
            <Link
              to="/contact"
              className="bg-primary hover:bg-primary-700 text-white px-6 py-2.5 rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg flex items-center space-x-2"
            >
              <EnvelopeIcon className="h-5 w-5" />
              <span>Contact Us</span>
            </Link>
          </div>

          <div className="lg:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="bg-gray-50 p-2 rounded-md text-gray-700 hover:text-primary hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            >
              {isOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>
    </nav>

    {/* Mobile Menu */}
    {isOpen && (
      <div className="lg:hidden fixed top-[92px] sm:top-[90px] left-0 right-0 bg-white shadow-lg z-30 max-h-[calc(100vh-92px)] sm:max-h-[calc(100vh-90px)] overflow-y-auto">
          <div className="px-2 pt-2 pb-3 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setIsOpen(false)}
                className={`flex items-center space-x-3 px-3 py-3 rounded-md text-base font-medium transition-colors duration-200 ${
                  isActive(item.href)
                    ? 'bg-primary text-white'
                    : 'text-gray-700 hover:bg-primary-50 hover:text-primary'
                }`}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.name}</span>
              </Link>
            ))}
            <Link
              to="/contact"
              onClick={() => setIsOpen(false)}
              className="flex items-center justify-center space-x-2 mt-4 bg-primary hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 w-full"
            >
              <EnvelopeIcon className="h-5 w-5" />
              <span>Contact Us</span>
            </Link>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
